import {
  WhiteInstantSearchBuilder,
  NoResults,
  mappers,
} from '@white-nl/instantsearch-components';
import { HitComponent } from '@white-nl/instantsearch-components/dist/types/components';
import InfiniteHits from '@white-nl/instantsearch-components/dist/components/results/InfiniteHits';
import { Hit, Loading } from '../components/projects/results';
import RefinementListPanel from '../components/projects/refinements/RefinementListPanel';
import CurrentRefinements from '../components/projects/refinements/CurrentRefinements/CurrentRefinements';

const settings = document.getElementById('projects-overview')?.dataset || null;
const whiteInstantSearchProjects = new WhiteInstantSearchBuilder();
if (settings) {
  const apiUrl = Craft.getSiteUrl('api/projects');

  fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  }).then((res) => res.json())
    .then((data) => {
      const {
        translates,
        filtersSorting,
        filters,
      } = data;

      let previousPage = 0;

      const { projects } = translates;

      const indexName = settings.indexName || '';

      whiteInstantSearchProjects
        .withSearchkit(
          indexName,
          settings.publicUrl || '',
          settings.jwt || '',
          settings.refreshUrl || '',
        )
        .withWhiteInstantSearchRouting({
          baseUrl: settings.baseUrl,
          addToPath: true,
          addTrailingSlashesToUrls: Craft.trailingSlashes === true,
          writeDelay: 0,
        })
        .addAdditionalData({ translates, titleSize: settings.titleSize })
        .addStatsComponent([{ component: ({ stats }) => stats.nbHits }], '.hits-count')
        .addCurrentRefinementsComponent(
          [{
            component: ({ currentRefinements }) => (
              currentRefinements.items.reduce((a, b) => a + b.refinements.length, 0)
            ),
          }],
          '.filters-count',
        )
        .addCurrentRefinementsComponent([
          {
            settings: {
              classNames: {
                rootList: 'flex flex-wrap gap-2',
                listItem: 'flex flex-wrap gap-2',
                list: 'flex flex-wrap gap-2',
                link: 'inline-flex items-center gap-x-2.5 text-sm leading-normal font-semibold rounded-full bg-white text-grey-400 py-1 px-3 border border-grey-400 transition-all duration-300 whitespace-nowrap no-underline hover:inset-ring-1 hover:inset-ring-grey-400 focus:inset-ring-1 focus:inset-ring-grey-400 focus:outline-none after:w-3 after:content-(--icon-xmark)',
              },
              transformItems: (items) => (!items ? [] : items.map((item) => ({
                ...item,
                label: filters[item.label] || item.label,
                refinements: item.refinements.map((refinement) => ({
                  ...refinement,
                  label: filters[refinement.label] || refinement.label,
                })),
              }))),
            },
            component: CurrentRefinements,
          },
        ]);
      Object.entries(filters).forEach(([key, value]) => {
        whiteInstantSearchProjects
          .addRefinementList([
            {
              settings: {
                attribute: key,
                classNames: {
                  label: 'group/label flex items-center relative min-h-8 underline underline-offset-2 decoration-transparent transition-all duration-300 cursor-pointer text-grey-700 text-base leading-normal underline-offset-2 decoration-from-font pr-10.5 hover:decoration-grey-700 has-focus:decoration-grey-700',
                  checkbox: 'absolute opacity-0',
                  count: 'hidden',
                  item: 'pt-2 pr-2',
                  list: '',
                  root: '',
                },
                searchable: false,
                showMore: false,
                transformItems: mappers.transformers.mapValues(filters),
                sortBy: (a, b) => {
                  const positionA = filtersSorting[a.escapedValue] || -1;
                  const positionB = filtersSorting[b.escapedValue] || -1;

                  if (positionA > positionB) {
                    return 1;
                  }
                  if (positionA < positionB) {
                    return -1;
                  }

                  return 0;
                },
                translations: {
                  showMoreButtonText({ isShowingMore }) {
                    return isShowingMore ? Craft.t(projects, 'Show less') : Craft.t(projects, 'Show more');
                  },
                  submitButtonTitle: Craft.t(projects, 'Submit the search query'),
                  resetButtonTitle: Craft.t(projects, 'Clear the search query'),
                },
                panelSettings: {
                  collapsiblePanelSettings: {
                    classNames: {
                      root: 'mb-10 last:mb-0',
                      header: 'mb-4 last:mb-0 text-grey-400 text-base leading-normal tracking-widest uppercase',
                    },
                    defaultExpanded: true,
                    canCollapse: false,
                  },
                  header: value as string,
                  hideIfEmpty: true,
                },
              },
              component: RefinementListPanel,
            },
          ]);
      });
      whiteInstantSearchProjects
        .addHitComponent([
          {
            settings: {
              loading: {
                translations: {
                  loading: Craft.t(projects, 'Loading...'),
                },
              },
              noResults: {
                translations: {
                  noResults: Craft.t(projects, 'Sorry, we can\'t find any projects!'),
                  refinements: Craft.t(projects, 'Try to choose another filter.'),
                  query: Craft.t(projects, 'Please try another query.'),
                },
              },
              infiniteHits: {
                translations: {
                  showPreviousButtonText: Craft.t(projects, 'Show previous projects'),
                  showMoreButtonText: Craft.t(projects, 'Show more projects'),
                },
                classNames: {
                  list: 'flex flex-wrap gap-y-4.5 md:gap-y-6 lg:gap-y-8 md:-mx-2 lg:-mx-4',
                  loadPrevious: 'btn btn-dark mb-8',
                  loadMore: 'btn btn-dark mt-8',
                  disabledLoadPrevious: 'hidden',
                  disabledLoadMore: 'hidden',
                },
                showPrevious: true,
              },
            },
            component: Hit as HitComponent,
            loadingComponent: Loading,
            noResultsComponent: NoResults,
            infiniteScrolling: true,
            infiniteHitsComponent: InfiniteHits,
          },
        ])
        .setHitsPerPage(15)
        .where(`siteId:${Craft.siteId}`)
        .afterRender((results) => {
          if (results.page !== 0) {
            if (results.page !== previousPage) {
              const firstNewHit = document.querySelector(`[data-page="${results.page}"]+div.w-full.md\\:px-2.lg\\:px-4>a`) as HTMLAnchorElement;
              if (firstNewHit) {
                firstNewHit.focus();
              }
            }
            previousPage = results.page;
          }
        })
        .build()
        .show();
    });
}
